// Toast notification component and utilities
import toast, { Toaster } from "react-hot-toast";
import { getApiErrorMessage, getSupportEmailHref, isServerIssueError } from "@utils/apiErrorUtils";
import { feedback } from "@/theme/colors";

/**
 * Toast Notifications Component
 * Add this once in your App.js or main layout
 */
export function ToastContainer() {
  return (
    <Toaster
      position="top-right"
      reverseOrder={false}
      gutter={12}
      toastOptions={{
        // Default options
        duration: 4000,
        style: {
          background: feedback.base.bg,
          color: feedback.base.text,
          padding: "16px 20px",
          borderRadius: "12px",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          border: `1px solid ${feedback.base.border}`,
          fontSize: "14px",
          fontWeight: "500",
          maxWidth: "420px",
        },
        // Success toast style
        success: {
          duration: 3000,
          style: {
            background: feedback.success.bg,
            color: feedback.success.text,
            border: `1px solid ${feedback.success.border}`,
          },
          iconTheme: {
            primary: feedback.success.icon,
            secondary: feedback.base.bg,
          },
        },
        // Error toast style
        error: {
          duration: 4500,
          style: {
            background: feedback.error.bg,
            color: feedback.error.text,
            border: `1px solid ${feedback.error.border}`,
          },
          iconTheme: {
            primary: feedback.error.icon,
            secondary: feedback.base.bg,
          },
        },
        // Loading toast style
        loading: {
          style: {
            background: feedback.info.bg,
            color: feedback.info.text,
            border: `1px solid ${feedback.info.border}`,
          },
          iconTheme: {
            primary: feedback.info.icon,
            secondary: feedback.base.bg,
          },
        },
      }}
    />
  );
}

/**
 * Toast Notification Utilities
 * Use these functions to show toasts
 */

// Success toast
export const showSuccess = (message) => {
  toast.success(message);
};

// Error toast
export const showError = (message) => {
  toast.error(message);
};

export const showApiError = (error) => {
  const message = getApiErrorMessage(error);
  const supportEmailHref = getSupportEmailHref(error);

  if (!isServerIssueError(error) || !supportEmailHref) {
    toast.error(message);
    return;
  }

  toast.custom(
    (t) => (
      <div className="w-full max-w-[440px] rounded-xl border border-palette-red-ring bg-palette-red-soft p-4 shadow-lg">
        <div className="text-sm font-semibold text-palette-red-text">Server Error</div>
        <div className="mt-1 text-sm text-palette-red-heading">{message}</div>
        <div className="mt-3 flex justify-end">
          <a
            href={supportEmailHref}
            className="inline-flex items-center rounded-md border border-palette-red-indicator bg-white px-3 py-2 text-xs font-semibold text-palette-red-heading hover:bg-palette-red-muted"
            onClick={() => toast.dismiss(t.id)}
          >
            Contact Dev Team
          </a>
        </div>
      </div>
    ),
    { duration: 12000 },
  );
};

// Info toast
export const showInfo = (message) => {
  toast(message, {
    icon: "ℹ️",
    style: {
      background: feedback.info.bg,
      color: feedback.info.text,
      border: `1px solid ${feedback.info.border}`,
    },
  });
};

// Warning toast
export const showWarning = (message) => {
  toast(message, {
    icon: "⚠️",
    style: {
      background: feedback.warning.bg,
      color: feedback.warning.text,
      border: `1px solid ${feedback.warning.border}`,
    },
  });
};

// Loading toast (returns toast id for dismissal)
export const showLoading = (message) => {
  return toast.loading(message);
};

// Dismiss specific toast
export const dismissToast = (toastId) => {
  toast.dismiss(toastId);
};

// Dismiss all toasts
export const dismissAllToasts = () => {
  toast.dismiss();
};

// Promise-based toast (auto updates based on promise state)
export const showPromise = (promise, messages) => {
  return toast.promise(promise, {
    loading: messages.loading || "Loading...",
    success: messages.success || "Success!",
    error: messages.error || "Error occurred",
  });
};

// Custom toast with custom styling
export const showCustom = (message, options = {}) => {
  return toast(message, options);
};

// Export the base toast for advanced usage
export { toast };
